import { Link } from "react-router-dom";
import { Star, Clock, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

// Force Unsplash images to be served small + WebP so the card doesn't pull 3MB+
const optimizeImageUrl = (url, width = 400) => {
  if (!url || typeof url !== "string") return url;
  if (!url.includes("images.unsplash.com")) return url;
  const [base] = url.split("?");
  return `${base}?w=${width}&q=65&fm=webp&fit=crop&auto=format`;
};

const TourCard = ({ tour, variant = "default" }) => {
  const isLarge = variant === "large";
  const isFeatured = variant === "featured";
  const { t, i18n } = useTranslation();
  const isES = i18n.language?.startsWith("es");
  const formatPrice = (usd) => isES ? `MX$${Math.round(usd * 17.5).toLocaleString()}` : `$${usd.toFixed(0)}`;

  const categoryKey = `tour_card.cat_${tour.category}`;

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={`tour-card group flex flex-col h-full ${
        isLarge ? "row-span-2" : ""
      } ${isFeatured ? "md:col-span-2" : ""}`}
      data-testid={`tour-card-${tour.slug}`}
      itemScope
      itemType="https://schema.org/Product"
    >
      {/* Image Container */}
      <div className={`relative overflow-hidden flex-shrink-0 ${isLarge ? "h-48 sm:h-64" : "h-40 sm:h-48"}`}>
        <img
          src={optimizeImageUrl(tour.image_url, 400)}
          srcSet={`${optimizeImageUrl(tour.image_url, 400)} 400w, ${optimizeImageUrl(tour.image_url, 600)} 600w`}
          sizes="(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 380px"
          alt={`${tour.title} — Cancun tour photo`}
          width="400"
          height="300"
          className="tour-card-image w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          itemProp="image"
        />
        
        {/* Category Badge */}
        <span className="absolute top-3 left-3 category-pill bg-white/90 backdrop-blur-sm text-xs">
          {t(categoryKey)}
        </span>

        {/* Featured Badge */}
        {tour.featured && (
          <span className="absolute top-3 right-3 px-2 py-1 bg-[#E29578] text-white text-[10px] font-bold rounded-full">
            {t("tour_card.featured")}
          </span>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 gradient-overlay opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-grow">
        {/* Rating */}
        <div className="flex items-center gap-1.5 mb-2">
          <div className="star-rating flex">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-3 h-3 ${
                  i < Math.floor(tour.rating) ? "star-filled fill-current" : "star-empty"
                }`}
              />
            ))}
          </div>
          <span className="text-xs font-medium text-[#1D3557]">
            {tour.rating.toFixed(1)}
          </span>
          <span className="text-xs text-[#64748B]">
            ({tour.review_count.toLocaleString()})
          </span>
        </div>

        {/* Title */}
        <h3 className="font-heading font-semibold text-sm sm:text-base text-[#1D3557] mb-2 group-hover:text-[#006D77] transition-colors line-clamp-2 min-h-[2.5rem] sm:min-h-[3rem]">
          <Link to={`/tours/${tour.slug}`} data-testid={`tour-link-${tour.slug}`}>
            {tour.title}
          </Link>
        </h3>

        {/* Description */}
        <p className="text-xs text-[#64748B] mb-3 line-clamp-2 flex-grow">
          {tour.short_description}
        </p>

        {/* Meta Info */}
        <div className="flex items-center gap-3 text-xs text-[#64748B] mb-3">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {tour.duration}
          </span>
          <span className="flex items-center gap-1 truncate">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{tour.meeting_point.split(",")[0]}</span>
          </span>
        </div>

        {/* Price & CTA - Fixed at bottom */}
        <div className="flex items-center justify-between pt-3 border-t border-[#E2E8F0] mt-auto">
          <div>
            <span className="text-[10px] text-[#64748B] block">{t("tour_card.from")}</span>
            <p className="font-heading font-bold text-lg text-[#006D77]">
              {formatPrice(tour.price)}
            </p>
          </div>
          <Link
            to={`/tours/${tour.slug}`}
            data-testid={`tour-details-btn-${tour.slug}`}
            className="px-3 py-2 text-xs font-semibold text-white bg-[#006D77] rounded-full hover:bg-[#005a63] transition-colors"
          >
            {t("tour_card.details")}
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

export default TourCard;
